import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { CalendarClock, PlayCircle, X, Loader2, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { formatCurrency, formatDateShort } from '@/lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const STATUS_LABELS = {
  pending: 'À venir',
  executed: 'Exécutée',
  cancelled: 'Annulée',
  failed: 'Échouée',
};

const STATUS_COLORS = {
  pending: 'bg-blue-100 text-blue-700',
  executed: 'bg-emerald-100 text-emerald-700',
  cancelled: 'bg-slate-100 text-slate-500',
  failed: 'bg-red-100 text-red-700',
};

const MODE_LABELS = {
  prelevement: 'Prélèvement SEPA',
  virement: 'Virement',
  virement_sepa: 'Virement SEPA (XML)',
  cheque: 'Chèque',
  carte: 'Carte bancaire',
};

export default function ScheduledPayments() {
  const { hasPermission } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('all');
  const [processing, setProcessing] = useState(false);

  const canPay = hasPermission('invoices', 'pay');

  const fetchItems = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/scheduled-payments/`);
      setItems(res.data || []);
    } catch (e) {
      toast.error('Erreur chargement paiements programmés');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchItems(); }, []);

  const today = new Date().toISOString().slice(0, 10);

  const filtered = useMemo(() => {
    if (tab === 'all') return items;
    if (tab === 'a_venir') return items.filter(i => i.status === 'pending' && i.scheduled_date > today);
    if (tab === 'echues') return items.filter(i => i.status === 'pending' && i.scheduled_date <= today);
    if (tab === 'executed') return items.filter(i => i.status === 'executed');
    if (tab === 'cancelled') return items.filter(i => i.status === 'cancelled');
    if (tab === 'failed') return items.filter(i => i.status === 'failed');
    return items;
  }, [items, tab, today]);

  const dueCount = useMemo(
    () => items.filter(i => i.status === 'pending' && i.scheduled_date <= today).length,
    [items, today]
  );

  const handleProcessDue = async () => {
    if (dueCount === 0) {
      toast.info("Aucun paiement échu à exécuter aujourd'hui");
      return;
    }
    if (!window.confirm(`Exécuter ${dueCount} paiement(s) échu(s) maintenant ? Cette action est définitive.`)) return;
    setProcessing(true);
    try {
      const res = await axios.post(`${API_URL}/scheduled-payments/process-due`);
      const { executed, failed, skipped } = res.data;
      const parts = [`${executed} exécuté(s)`];
      if (failed) parts.push(`${failed} échec(s)`);
      if (skipped) parts.push(`${skipped} ignoré(s)`);
      toast.success(parts.join(' · '));
      await fetchItems();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur exécution');
    } finally { setProcessing(false); }
  };

  const handleCancel = async (item) => {
    if (!window.confirm(`Annuler le paiement programmé du ${formatDateShort(item.scheduled_date)} ?`)) return;
    try {
      await axios.delete(`${API_URL}/scheduled-payments/${item.id}`);
      toast.success('Paiement annulé');
      await fetchItems();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur annulation');
    }
  };

  const handleExecuteOne = async (item) => {
    if (!window.confirm(`Exécuter ce paiement maintenant (${formatCurrency(item.montant_ttc || 0)} à ${item.supplier_name}) ?`)) return;
    try {
      const res = await axios.post(`${API_URL}/scheduled-payments/${item.id}/execute-now`);
      if (res.data.status === 'failed') {
        toast.error(`Échec : ${res.data.error || 'erreur inconnue'}`);
      } else {
        toast.success('Paiement exécuté');
      }
      await fetchItems();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur exécution');
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white flex items-center gap-2">
            <CalendarClock className="w-6 h-6 text-[#0F5156]" />
            Paiements programmés
          </h1>
          <p className="text-slate-500 mt-1">{items.length} paiement(s) — {dueCount} échue(s) aujourd'hui</p>
        </div>
        {canPay && (
          <Button onClick={handleProcessDue} disabled={processing || dueCount === 0} className="bg-[#0F5156] hover:bg-[#0d464a]">
            {processing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <PlayCircle className="w-4 h-4 mr-2" />}
            Tout exécuter ({dueCount})
          </Button>
        )}
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="all">Toutes</TabsTrigger>
          <TabsTrigger value="echues">Échues aujourd'hui</TabsTrigger>
          <TabsTrigger value="a_venir">À venir</TabsTrigger>
          <TabsTrigger value="executed">Exécutées</TabsTrigger>
          <TabsTrigger value="failed">Échouées</TabsTrigger>
          <TabsTrigger value="cancelled">Annulées</TabsTrigger>
        </TabsList>
      </Tabs>

      <Card><CardContent className="p-0">
        {loading ? (
          <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin" /></div>
        ) : filtered.length === 0 ? (
          <div className="py-12 text-center text-slate-500">Aucun paiement dans cet onglet</div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Date prévue</TableHead>
                <TableHead>Fournisseur</TableHead>
                <TableHead>N° Facture</TableHead>
                <TableHead>Montant</TableHead>
                <TableHead>Mode</TableHead>
                <TableHead>Statut</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map(item => (
                <TableRow key={item.id} className={item.status === 'pending' && item.scheduled_date <= today ? 'bg-amber-50/50' : ''}>
                  <TableCell className="font-medium">{formatDateShort(item.scheduled_date)}</TableCell>
                  <TableCell>{item.supplier_name || '-'}</TableCell>
                  <TableCell className="font-mono text-sm">{item.internal_ref || item.invoice_number || '-'}</TableCell>
                  <TableCell className="font-medium">{formatCurrency(item.montant_ttc || 0)}</TableCell>
                  <TableCell>{MODE_LABELS[item.mode_paiement] || item.mode_paiement}</TableCell>
                  <TableCell>
                    <Badge className={STATUS_COLORS[item.status] || 'bg-slate-100'}>
                      {STATUS_LABELS[item.status] || item.status}
                    </Badge>
                    {item.status === 'failed' && item.error_message && (
                      <p className="text-[10px] text-red-600 mt-1 max-w-xs truncate" title={item.error_message}>
                        <AlertTriangle className="inline w-3 h-3 mr-0.5" />{item.error_message}
                      </p>
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    {canPay && item.status === 'pending' && (
                      <div className="inline-flex gap-1">
                        <Button size="sm" variant="outline" onClick={() => handleExecuteOne(item)} className="text-green-700 border-green-300">
                          <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Exécuter
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => handleCancel(item)} className="text-slate-500">
                          <X className="w-3.5 h-3.5 mr-1" /> Annuler
                        </Button>
                      </div>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent></Card>
    </div>
  );
}
